
import React from 'react'
import { Badge } from 'reactstrap'
import PropTypes from 'prop-types'

/**
 * IterationBadge - Visual badge for auction iteration display.
 * 
 * BRD: Each iteration has distinct visual styling to help users quickly
 * identify where an auction is in its lifecycle.
 * 
 * Iteration Flow: MBP → IT2 → IT3 → IT4 → OpenLane
 */

/**
 * Iteration color mapping based on progression
 */
const ITERATION_STYLES = {
	'MBP': {
		color: 'primary',
		label: 'MBP',
		tooltip: 'Market-Based Pricing',
		background: '#007bff',
		textColor: '#fff'
	},
	'IT2': {
		color: 'info',
		label: 'IT2',
		tooltip: 'Iteration 2',
		background: '#17a2b8',
		textColor: '#fff'
	},
	'IT3': {
		color: 'warning',
		label: 'IT3',
		tooltip: 'Iteration 3',
		background: '#ffc107',
		textColor: '#212529'
	},
	'IT4': {
		color: 'danger',
		label: 'IT4',
		tooltip: 'Iteration 4',
		background: '#dc3545',
		textColor: '#fff'
	},
	'OpenLane': {
		color: 'dark',
		label: 'OpenLane',
		tooltip: 'OpenLane Auction',
		background: '#343a40',
		textColor: '#fff'
	}
}

/**
 * Map iteration ID to iteration name
 */
const ITERATION_ID_MAP = {
	1: 'MBP',
	2: 'IT2',
	3: 'IT3',
	4: 'IT4',
	5: 'OpenLane'
}

const IterationBadge = ({ 
	iteration, 
	iterationId, 
	size = 'normal',
	showTooltip = false,
	className = '' 
}) =>
{
	// Determine iteration name from either prop
	let iterationName = iteration
	if (!iterationName && iterationId) {
		iterationName = ITERATION_ID_MAP[iterationId]
	}
	
	// Handle various iteration name formats
	if (iterationName) {
		// Normalize to known values
		const normalized = iterationName.toString().toUpperCase().replace(/\s+/g, '')
		if (normalized.includes('MBP') || normalized === 'MARKETBASEDPRICING' || normalized === '1') {
			iterationName = 'MBP'
		} else if (normalized === 'IT2' || normalized === 'ITERATION2' || normalized === '2') {
			iterationName = 'IT2'
		} else if (normalized === 'IT3' || normalized === 'ITERATION3' || normalized === '3') {
			iterationName = 'IT3'
		} else if (normalized === 'IT4' || normalized === 'ITERATION4' || normalized === '4') {
			iterationName = 'IT4'
		} else if (normalized.includes('OPENLANE') || normalized === '5') {
			iterationName = 'OpenLane'
		}
	}
	
	// Get styles for this iteration
	const style = ITERATION_STYLES[iterationName] || {
		color: 'secondary',
		label: iterationName || 'Unknown',
		tooltip: iterationName || 'Unknown Iteration',
		background: '#6c757d',
		textColor: '#fff'
	}
	
	// Size styles
	const sizeStyles = {
		small: { fontSize: '0.65rem', padding: '0.15rem 0.35rem' },
		normal: { fontSize: '0.75rem', padding: '0.25rem 0.5rem' },
		large: { fontSize: '0.85rem', padding: '0.35rem 0.65rem' }
	}
	
	const badgeStyle = {
		...sizeStyles[size] || sizeStyles.normal,
		fontWeight: 600,
		letterSpacing: '0.5px'
	}
	
	if (!iterationName) {
		return null
	}
	
	return (
		<Badge
			color={style.color}
			className={`iteration-badge ${className}`}
			style={badgeStyle}
			title={showTooltip ? style.tooltip : undefined}
		>
			{style.label}
		</Badge>
	)
}

IterationBadge.propTypes = {
	/** Iteration name string (e.g., 'MBP', 'IT2', 'IT3', 'IT4', 'OpenLane') */
	iteration: PropTypes.string,
	/** Iteration ID number (1-5) - alternative to iteration prop */
	iterationId: PropTypes.number,
	/** Badge size: 'small', 'normal', or 'large' */
	size: PropTypes.oneOf(['small', 'normal', 'large']),
	/** Show tooltip on hover */
	showTooltip: PropTypes.bool,
	/** Additional CSS class */
	className: PropTypes.string
}

export default IterationBadge
